import { Box, Flex, Text } from '@chakra-ui/react'
import React from 'react'
import { Cell, Pie, PieChart } from 'recharts'
import { colorPalette } from '~/themes/colors/colorPalette'
import { textStyleKeys } from '~/themes/textStyles'
import { CallTranscript } from '~/types/CallTranscript'
import { convertCallDuration } from '~/utils/formatter'

import { SectionHeader } from './SectionHeader'

interface OfficeTalkTimeSectionProps {
  transcript: CallTranscript
}

export const OfficeTalkTimeSection = ({
  transcript,
}: OfficeTalkTimeSectionProps) => {
  const agentTalkTimeMillis =
    transcript.ConversationCharacteristics.TalkTime.DetailsByParticipant.AGENT
      ?.TotalTimeMillis ?? 0
  const agentTalkTimeSeconds = (agentTalkTimeMillis / 1000) | 0

  const customerTalkTimeMillis =
    transcript.ConversationCharacteristics.TalkTime.DetailsByParticipant
      .CUSTOMER?.TotalTimeMillis ?? 0
  const customerTalkTimeSeconds = (customerTalkTimeMillis / 1000) | 0

  const totalTalkTimeSeconds = agentTalkTimeSeconds + customerTalkTimeSeconds
  const agentTalkTimePercentage = totalTalkTimeSeconds
    ? agentTalkTimeSeconds / totalTalkTimeSeconds
    : 0
  const customerTalkTimePercentage = totalTalkTimeSeconds
    ? customerTalkTimeSeconds / totalTalkTimeSeconds
    : 0

  const talkingColor = colorPalette.gray[900]
  const listeningColor = colorPalette.gray[600]

  return (
    <Flex
      flexDirection="column"
      width="100%"
      gap="0.5rem"
      justifyItems="stretch"
    >
      <SectionHeader
        title="Office Talk Time"
        tooltipText="This pie chart shows a breakdown of the office's time spent talking versus listening during the call. Ideally, agents should spend more time listening than talking, aiming for a 50-60% talking and 40-50% listening split for effective customer interaction."
      />
      <Flex flexDirection="row" justifyContent="flex-start">
        <Flex flexDirection="column" width="50%" gap="0.5rem">
          <Flex flexDirection="row" gap="0.5rem" alignItems="center">
            <Box
              textStyle={textStyleKeys.Body3}
              height="1rem"
              width="1rem"
              borderRadius="0.5rem"
              backgroundColor={talkingColor}
            ></Box>
            <Flex flexDirection="column">
              <Text textStyle={textStyleKeys.Body3}>Talking</Text>
              <Text textStyle={textStyleKeys.Body3}>
                {convertCallDuration(agentTalkTimeSeconds)} (
                {Math.round(agentTalkTimePercentage * 100).toFixed(0)}%)
              </Text>
            </Flex>
          </Flex>
          <Flex flexDirection="row" gap="0.5rem" alignItems="center">
            <Box
              textStyle={textStyleKeys.Body3}
              height="1rem"
              width="1rem"
              borderRadius="0.5rem"
              backgroundColor={listeningColor}
            ></Box>
            <Flex flexDirection="column">
              <Text textStyle={textStyleKeys.Body3}>Listening</Text>
              <Text textStyle={textStyleKeys.Body3}>
                {convertCallDuration(customerTalkTimeSeconds)} (
                {Math.round(customerTalkTimePercentage * 100).toFixed(0)}%)
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex width="4.75rem" height="4.75rem">
          <PieChart
            width={76}
            height={76}
            margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <Pie
              data={[
                { name: 'Agent', value: agentTalkTimeSeconds },
                { name: 'Customer', value: customerTalkTimeSeconds },
              ]}
              dataKey="value"
              cx="50%"
              cy="50%"
              outerRadius="38"
              fill="#8884d8"
            >
              <Cell fill={talkingColor} name="Agent" />
              <Cell fill={listeningColor} name="Customer" />
            </Pie>
          </PieChart>
        </Flex>
      </Flex>
    </Flex>
  )
}
